import React, { useState, useEffect } from "react";
import { 
  Grid, Box, Typography, Button, TextField, 
  IconButton, Modal, Paper, Divider,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";
import axios from "axios";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNotification } from "../../components/NotificationContext";
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 24px)', sm: '80%', md: '700px' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 1,
  p: { xs: 2, sm: 4 },
  maxHeight: '90vh',
  overflowY: 'auto'
};

const CategoryEdit = () => {
  const navigate = useNavigate();
  const showNotification = useNotification();
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editedMainCategory, setEditedMainCategory] = useState("");
  const [editedSubCategories, setEditedSubCategories] = useState([]);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [loading, setLoading] = useState(false);

  // Kategorileri getir
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories`);
        setCategories(response.data);
      } catch (error) {
        showNotification('kategoriLer Yüklenemedi!', 'error');
      }
    };
    fetchCategories();
  }, []);

  // Modal açma ve kategori seçme
  const handleOpen = (category) => {
    setSelectedCategory(category);
    setEditedMainCategory(category.name);
    setEditedSubCategories([...category.subCategories]);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  // Ana kategori adını güncelle
  const handleMainCategoryChange = (e) => {
    setEditedMainCategory(e.target.value);
  };

  // Alt kategori adını güncelle
  const handleSubCategoryChange = (index, value) => {
    const updatedSubs = [...editedSubCategories];
    updatedSubs[index].name = value;
    setEditedSubCategories(updatedSubs);
  };

  const handleAddSubCategory = () => {
    if (newSubCategory.trim()) {
      // Tüm alt kategorileri birleştir (hem backend'den gelenler hem yeni eklenenler)
      const allSubCategories = [
        ...categories.flatMap(cat => cat.subCategories),
        ...editedSubCategories
      ];
      
      // En yüksek ID'yi bul
      const maxId = allSubCategories.reduce(
        (max, sub) => Math.max(max, sub?.id || 0), 
        0
      );
      
      // Yeni ID'yi oluştur (maxId + 1)
      const newId = maxId + 1;
      
      setEditedSubCategories([
        ...editedSubCategories,
        { id: newId, name: newSubCategory.trim() }
      ]);
      setNewSubCategory("");
    }
  };
  // Alt kategori sil
  const handleRemoveSubCategory = (id) => {
    setEditedSubCategories(editedSubCategories.filter(sub => sub.id !== id));
  };

  // Kategori güncelleme fonksiyonu
  const handleUpdateCategory = async () => {
    if (!editedMainCategory.trim()) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/categories/update/${selectedCategory.id}`,
        {
          mainCategory: editedMainCategory,
          subCategories: editedSubCategories
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // State'i güncelle
      setCategories(categories.map(cat => 
        cat.id === selectedCategory.id 
          ? { 
              ...cat, 
              name: response.data.data.name,
              subCategories: response.data.data.subCategories 
            } 
          : cat
      ));
      showNotification('Kategori başarıyla Güncellendi!', 'success');
      handleClose();
    } catch (error) {
      showNotification('kategori Güncelleme Başarısız!', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Kategori silme fonksiyonu
  const handleDeleteCategory = async (id) => {
    if (window.confirm("Bu kategoriyi silmek istediğinize emin misiniz?")) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/categories/delete/${id}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        setCategories(categories.filter(cat => cat.id !== id));
        handleClose();
        showNotification('Kategori başarıyla silindi!', 'success');
      } catch (error) {
        showNotification('kategori Silme Başarısız!', 'error');
      }
    }
  };
  const handleBackClick = () => {
    navigate(-1);
};
  return (
    <Grid container justifyContent="center" alignItems="center" sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, md: 4 } }}>
    <Grid  size={{ xs: 12, sm: 10,md:10 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1, backgroundColor: '#ffffff' }}>
        <IconButton onClick={handleBackClick} style={{ marginRight: '10px' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" gutterBottom align="center" style={{ fontWeight: 600, color: '#3f51b5' }}>
          Kategori Düzenle
        </Typography>
  
        <Grid container spacing={2}>
          {categories?.map((category) => (
            <Grid  size={{ xs: 12, sm: 6,md:4 }} key={category.id}>
              <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    borderRadius: 2,
                    boxShadow: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer"
                  }}
                  onClick={() => handleOpen(category)}
                >
                  <Typography variant="h6">{category.name}</Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Grid>
  
    {/* Düzenleme Modalı */}
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        {selectedCategory && (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2
            }}>
              <Typography id="modal-modal-title" variant="h5">
                Kategori Düzenle
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <TextField
              label="Ana Kategori Adı"
              variant="outlined"
              fullWidth
              value={editedMainCategory}
              onChange={handleMainCategoryChange}
              sx={{ mb: 3 }}
            />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              Alt Kategoriler
            </Typography>
            
            {editedSubCategories.map((sub, index) => (
              <Box key={sub.id} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 1 
              }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={sub.name}
                  onChange={(e) => handleSubCategoryChange(index, e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <IconButton 
                  color="error"
                  onClick={() => handleRemoveSubCategory(sub.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            
            <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Yeni Alt Kategori"
                variant="outlined"
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSubCategory()}
              />
              <Button
                variant="contained"
                onClick={handleAddSubCategory}
                startIcon={<AddIcon />}
              >
                Ekle
              </Button>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              mt: 3
            }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDeleteCategory(selectedCategory.id)}
                disabled={loading}
              >
                Tüm Kategoriyi Sil
              </Button>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                >
                  İptal
                </Button>
                <Button
                  variant="contained"
                  onClick={handleUpdateCategory}
                  disabled={loading || !editedMainCategory.trim()}
                >
                  {loading ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  </Grid>

  );
};

export default CategoryEdit;
